import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { site, nav, secondaryNav } from "../data/site";
import { Logo } from "./Logo";
import { SocialLinks } from "./SocialLinks";
import { useLang } from "../i18n";

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-ink text-cream overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 pb-14 border-b border-white/10">
          <h2 className="font-heading font-light text-[13vw] leading-[0.9] lg:text-[7vw] tracking-tight">
            {t("Let's ")}<span className="text-peach">{t("talk.")}</span>
          </h2>
          <Link
            to="/contact"
            data-testid="footer-cta-btn"
            className="inline-flex items-center rounded-full bg-cream text-ink px-7 py-4 font-medium hover:bg-peach transition-colors duration-200 self-start lg:self-auto"
          >
            {t("Request Information")}
          </Link>
        </div>

        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4 pt-14">
          <div className="space-y-4">
            <Logo dark />
            <p className="text-sm text-cream/60 max-w-xs leading-relaxed">
              {t(site.tagline)}. {t("Reach out any time — we're here to help your family find the right next step.")}
            </p>
          </div>

          <div>
            <h3 className="font-heading text-sm uppercase tracking-widest text-cream/40 mb-4">
              {t("Explore")}
            </h3>
            <ul className="space-y-2.5">
              {[...nav, ...secondaryNav].map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-cream/70 hover:text-peach transition-colors text-[0.95rem]"
                  >
                    {t(item.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-sm uppercase tracking-widest text-cream/40 mb-4">
              {t("Contact")}
            </h3>
            <ul className="space-y-3 text-[0.95rem]">
              <li>
                <a href={site.phoneHref} data-testid="footer-phone" className="inline-flex items-center gap-2 text-cream/70 hover:text-peach transition-colors">
                  <Phone size={16} /> {site.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${site.email}`} data-testid="footer-email" className="inline-flex items-center gap-2 text-cream/70 hover:text-peach transition-colors">
                  <Mail size={16} /> {site.email}
                </a>
              </li>
              {site.clinics.map((c) => (
                <li key={c.address} className="flex items-start gap-2 text-cream/70" data-testid={`footer-address-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <MapPin size={16} className="mt-0.5 shrink-0 text-peach" />
                  <span>
                    <span className="block text-cream/90 text-sm">{c.name}</span>
                    <span className="block text-cream/60 text-sm">{c.address}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-sm uppercase tracking-widest text-cream/40 mb-4">
              {t("Follow")}
            </h3>
            <SocialLinks variant="dark" />
            <p className="text-xs text-cream/40 mt-4">@amenityhouse_ · @amenityhouse</p>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-cream/50">
          <p>© 2026 {site.name}. {t("All rights reserved.")}</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-peach transition-colors">
              {t("Privacy Policy")}
            </Link>
            <Link to="/accessibility" data-testid="footer-accessibility-link" className="hover:text-peach transition-colors">
              {t("Accessibility Statement")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
